import { filter, map, switchMap, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { ILegalEntity } from '@cemex-core/cmx-api/models/legal-entities/legal-entity';
import { TimeWindowsReq, TimeWindowsRes } from '@cemex-core/cmx-api/models/time-windows';
import { getFieldsForLoadingCatalogs, getLocationPage, OrderBuilderState } from '../../../../reducers';
import { BusinessLinesState } from '../../../business-line-selection/reducers/business-line/business-lines.reducer';
import { Plant, PlantsService, TimeWindowsService } from '@cemex-core/cmx-api';
import {
  LOAD_TIME_WINDOWS_FROM_PLANT,
  LoadTimeWindowsCompleteAction,
  LoadTimeWindowsFromPlantAction,
} from '../../actions/time-windows.actions';
import { LOAD_CONFIG_COMPLETE, SpecificationLoadConfigCompleteAction } from '../../actions/specification.actions';
import { ShippingConditionsState } from '../../../business-line-selection/reducers/shipping-condition/shipping-conditions.reducer';
import { EffectsRegistrationService } from '@ordersnproducts/modules/shared/modules/dynamic-effects/effects-registration.service';
import { UnicEffectsAvailableEnum } from '@ordersnproducts/modules/shared/modules/dynamic-effects/deletable-effect-sources.constant';

@Injectable()
export class TimeWindowsFromPlantEffects {
  specificationConfigLoadComplete: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_CONFIG_COMPLETE),
      filter(() => this.isEffectAvailable()),
      switchMap((payload: SpecificationLoadConfigCompleteAction) => {
        return this.store.select(getLocationPage).pipe(take(1));
      }),
      switchMap((locationPageState) => {
        const plantId = locationPageState.jobsite.jobsiteSalesArea[0].plant.plantId;
        return this.plantsService.getPlant(plantId).pipe(map((plant: Plant) => plant.plantCode));
      }),
      switchMap((plantCode: any) => {
        return this.store.select(getFieldsForLoadingCatalogs).pipe(
          take(1),
          map(
            (result: {
              customer: ILegalEntity;
              businessLineState: BusinessLinesState;
              shippingContitionState: ShippingConditionsState;
            }) => {
              return new LoadTimeWindowsFromPlantAction({
                countryCode: result.customer.countryCode,
                plantCode: plantCode,
                productLineCode: result.businessLineState.selected.productTypeCode,
                shippingConditionCode: result.shippingContitionState.selected.shippingConditionCode,
                customerCode: result.customer.legalEntityCode
                  ? result.customer.legalEntityCode
                  : result.customer.legalEntityTypeCode,
              } as TimeWindowsReq);
            },
          ),
        );
      }),
    ),
  );

  timeWindowLoad: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_TIME_WINDOWS_FROM_PLANT),
      switchMap((payload: LoadTimeWindowsFromPlantAction) => {
        return this.timeWindowsService.getTimeWindows(payload.payload).pipe(
          map((response: TimeWindowsRes) => {
            return new LoadTimeWindowsCompleteAction(response.timewindows);
          }),
        );
      }),
    ),
  );

  /**
   * Identifies if the effect is available to be executed
   * @returns {boolean}
   */
  private isEffectAvailable(): boolean {
    return this.effectRegister.isEffectAvailable(UnicEffectsAvailableEnum.TimeWindowsFromPlantEffects);
  }

  constructor(
    private actions$: Actions,
    private store: Store<OrderBuilderState>,
    private timeWindowsService: TimeWindowsService,
    private plantsService: PlantsService,
    private effectRegister: EffectsRegistrationService,
  ) {}
}
